// @ts-check
import { gql, useMutation, useQuery } from '@apollo/client';
import React from 'react';
import MapFields from '../MapFields/MapFields';
import Notification from '../Notification';
import SampleRows from './SampleRows';
import SelectHeaderAndData from './SelectHeaderAndDataRowIndices';

/**
 * Page where the header row and where data starts is defined.
 * Most of these attributes except importData are available in UseMapHeader.
 *
 * @export
 * @param {ReturnType<useSetHeaderAndDataRowIndices>} props
 * @return
 */
export default function SetHeaderAndDataRowIndices({
  loading,
  sampleRows,
  dataRowIndex,
  setDataRowIndex,
  headerRowIndex,
  setHeaderRowIndex,
  notification,
  cancelNotification,
}) {
  if (loading) return null;

  return (
    <React.Fragment>
      <SelectHeaderAndData
        sampleRows={sampleRows}
        headerRowIndex={headerRowIndex}
        dataRowIndex={dataRowIndex}
        onHeaderRowIndexChange={(headerRowIndex) => {
          setHeaderRowIndex(headerRowIndex);
        }}
        onDataRowIndexChange={(dataRowIndex) => {
          setDataRowIndex(dataRowIndex);
        }}
      />
      <SampleRows
        sx={{ marginTop: 2, height: 400 }}
        sampleRows={sampleRows}
        headerRowIndex={headerRowIndex}
        dataRowIndex={dataRowIndex}
        columnsLimit={5}
      />
      {notification && (
        <Notification
          onClose={() => cancelNotification()}
          message={notification.message}
          variant={notification.success ? 'success' : 'error'}
        />
      )}
    </React.Fragment>
  );
}

/**
 *
 * @param {object} params
 * @param {string} params.workspaceId
 * @param {string} params.importId
 * @returns
 */
export function useSetHeaderAndDataRowIndices({ workspaceId, importId }) {
  const queryResult = useQuery(query, {
    variables: { workspaceId, importId },
  });
  const { sampleRows, savedHeaderRowIndex, savedDataRowIndex } =
    afterQuery(queryResult);
  const [isSubmitting, setIsSubmitting] = React.useState(
    /** @type {boolean} */ (false)
  );
  const [isDirty, setIsDirty] = React.useState(/** @type {boolean} */ (false));

  const [_updateHeaderRow] = useMutation(UPDATE_HEADER_ROW);
  async function updateHeaderRow() {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { data } = await _updateHeaderRow({
        variables: {
          input: {
            importId,
            headerRowIndex,
            dataRowIndex,
          },
        },
      });
      if (!data?.result?.success) {
        setNotification({
          message: 'There was an error, please try again!',
        });
      }
      setIsDirty(false);
      setIsSubmitting(false);
    } catch (err) {
      setNotification({
        message: 'There was an error, please try again!',
      });
      setIsDirty(false);
      setIsSubmitting(false);
      return { error: err };
    }
  }

  const [headerRowIndex, setHeaderRowIndex] = React.useState(
    /** @type {number | undefined} */ (undefined)
  );
  const [dataRowIndex, setDataRowIndex] = React.useState(
    /** @type {number | undefined} */ (undefined)
  );

  /** @type {[import('../Notification').Notification | undefined, React.Dispatch<React.SetStateAction<import('../Notification').Notification | undefined>>]} */
  const [notification, setNotification] = React.useState();

  // Only set the first time. We have to do this because instead of using a form to manage state, we are managing it directly.
  if (!queryResult.loading && !isDirty) {
    setHeaderRowIndex(headerRowIndex || savedHeaderRowIndex);
    setDataRowIndex(dataRowIndex || savedDataRowIndex);
    setIsDirty(true);
  }

  return {
    importId,
    isValid: !isSubmitting && headerRowIndex && dataRowIndex,
    ...queryResult,
    sampleRows,
    updateHeaderRow,
    headerRowIndex,
    setHeaderRowIndex,
    dataRowIndex,
    setDataRowIndex,
    notification,
    cancelNotification: () => setNotification(undefined),
  };
}

SetHeaderAndDataRowIndices.fragment = gql`
  fragment SetHeaderAndDataRowIndices on DataImport {
    importId
    type
    source {
      ... on DataImportSourceTabular {
        headerRowIndex
        dataRowIndex
        rawRows(first: 7)
      }
    }
  }
`;

const query = gql`
  query MapHeaderPage($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        ...SetHeaderAndDataRowIndices
      }
      dataImports {
        nodes {
          importId
          type
          source {
            ... on DataImportSourceTabular {
              headerRowIndex
              dataRowIndex
            }
          }
        }
      }
    }
  }
  ${SetHeaderAndDataRowIndices.fragment}
`;

function afterQuery({ data }) {
  const source = data?.workspace?.dataImport?.source || {};

  return {
    sampleRows: source?.rawRows || [],
    savedHeaderRowIndex: source?.headerRowIndex,
    savedDataRowIndex: source?.dataRowIndex,
  };
}

export const UPDATE_HEADER_ROW = gql`
  mutation updateDataImportHeaderAndDataRows(
    $input: UpdateDataImportHeaderAndDataRowsInput!
  ) {
    result: updateDataImportHeaderAndDataRows(input: $input) {
      code
      success
      message
      dataImport {
        importId
        ...SetHeaderAndDataRowIndices
        # We also need to refresh the map fields data for the next step
        # Otherwise, it does not get updated if we make changes on this page
        ...MapFields
        ...MapFieldsSource
      }
    }
  }
  ${SetHeaderAndDataRowIndices.fragment}
  ${MapFields.fragment}
`;
